/*
 * @Author: Ryan
 * @Date: 2026-01-14
 * @Description: How to use section for AI Video Generator landing page
 */
import Image from 'next/image';
import React from 'react';

const CDN_BASE = 'https://assets.falcocut.ai/ai-video-generator';

interface StepData {
  number: number;
  title: string;
  description: string;
}

const steps: StepData[] = [
  {
    number: 1,
    title: 'Write a Text Prompt or Upload an Image',
    description:
      'Type a short description of the video you want, or upload an image you’d like to animate. FalcoCut can understand basic ideas and turn them into a starting point for your video.',
  },
  {
    number: 2,
    title: 'Pick the AI Model',
    description:
      'Choose from powerful models like Sora, Veo, or Kling to shape the look and motion of your video.',
  },
  {
    number: 3,
    title: 'Set the Duration and Resolution',
    description:
      'Select how long you want the video to be (4s, 8s, or 12s)  and choose the resolution(720p or 1080p) that fits your needs.',
  },
  {
    number: 4,
    title: 'Save and Share Your Video',
    description:
      'Click Start button to generate your video and download it once it’s ready. You can create more versions or share your final clip anywhere you need.',
  },
];

const StepItem: React.FC<StepData> = ({ number, title, description }) => (
  <div className="flex gap-4">
    {/* 步骤编号 */}
    <div className="flex-shrink-0 w-8 h-8 rounded-lg bg-[#7C3AED] flex items-center justify-center text-white font-semibold text-sm">
      {number}
    </div>
    {/* 步骤内容 */}
    <div className="flex flex-col gap-2">
      <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm leading-relaxed">{description}</p>
    </div>
  </div>
);

const HowToUseSection: React.FC = () => {
  return (
    <section className="w-full bg-white py-[60px] pad:py-[80px] sm:py-[100px] px-4 pad:px-8 sm:px-[65px]">
      <div className="max-w-[1190px] mx-auto">
        {/* 标题 */}
        <h2 className="text-2xl pad:text-3xl sm:text-[42px] font-bold text-center text-gray-900 mb-10 pad:mb-14 sm:mb-16 leading-tight">
          How to Make an AI Video with FalcoCut
        </h2>

        {/* 内容区域 */}
        <div className="flex flex-col pad:flex-row gap-8 pad:gap-12 sm:gap-16 items-center">
          {/* 左侧图片 */}
          <div className="w-full pad:w-1/2">
            <div className="relative rounded-2xl overflow-hidden shadow-lg bg-[#F8F9FC]">
              <Image
                src={`${CDN_BASE}/how-to-use.webp`}
                alt="FalcoCut AI Video Generator Interface"
                width={560}
                height={480}
                className="w-full h-auto object-contain"
              />
            </div>
          </div>

          {/* 右侧步骤 */}
          <div className="w-full pad:w-1/2 flex flex-col gap-6 pad:gap-8">
            {steps.map((step) => (
              <StepItem key={step.number} {...step} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowToUseSection;
